import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getAffiliateLinkBreakdown } from '../../Services/AffiliateInfo/AffiliateApi';
import GlobalPagination from '../../Components/Common/Globalpagination';

// Status styling configuration
const STATUS_CONFIG = {
    confirmed: {
        label: 'Confirmed',
        color: '#2E7D32',
        bg: '#E8F5E8',
        border: '#C8E6C9'
    },
    pending: {
        label: 'Pending',
        color: '#D97706',
        bg: '#FFFBEB',
        border: '#FDE68A'
    },
    on_hold: {
        label: 'On Hold',
        color: '#991B1B',
        bg: '#FEE2E2',
        border: '#F87171'
    },
    partially_reversed: {
        label: 'Partially Reversed',
        color: '#B91C1C',
        bg: '#FEF2F2',
        border: '#FECACA'
    },
    reversed: {
        label: 'Reversed',
        color: '#B91C1C',
        bg: '#FEF2F2',
        border: '#FECACA'
    }
};

const getStatusDetails = (state, stateLabel) => {
    const key = (state || '').toLowerCase();
    return (
        STATUS_CONFIG[key] || {
            label: stateLabel || state || 'Pending',
            color: '#64748B',
            bg: '#F8FAFC',
            border: '#E2E8F0'
        }
    );
};

// Currency formatter
const formatCurrency = (amount) => {
    const num = Number(amount) || 0;
    return `₹${num.toLocaleString('en-IN')}`;
};

// Date formatter
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const AffiliateCommissionLedger = ({ onBack, selectedAffiliate: propAffiliate, selectedLink: propLink }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const passedAffiliate = propAffiliate || location.state?.affiliate || null;
    const passedLink = propLink || location.state?.link || null;
    const urlLinkId = searchParams.get('linkId') || searchParams.get('id');

    const linkId =
        propLink?.id ||
        propLink?.linkId ||
        propLink?._id ||
        propLink?.raw?.id ||
        propLink?.raw?._id ||
        location.state?.link?.id ||
        location.state?.linkId ||
        location.state?.id ||
        passedAffiliate?.affiliateUserId ||
        passedAffiliate?.id ||
        urlLinkId ||
        1;

    // Data states
    const [ledgerList, setLedgerList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [pagination, setPagination] = useState({
        totalItems: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 25,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [summary, setSummary] = useState(null);

    // Fetch link breakdown directly from backend API
    const fetchLedger = useCallback(async (currentPage = 1, currentLimit = limit) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAffiliateLinkBreakdown(linkId, {
                page: currentPage,
                limit: currentLimit
            });

            let dataList = [];
            if (Array.isArray(response)) {
                dataList = response;
            } else if (response && typeof response === 'object') {
                if (Array.isArray(response.breakdown)) {
                    dataList = response.breakdown;
                } else if (Array.isArray(response.data)) {
                    dataList = response.data;
                } else if (response.data && Array.isArray(response.data.breakdown)) {
                    dataList = response.data.breakdown;
                } else if (response.data && Array.isArray(response.data.records)) {
                    dataList = response.data.records;
                } else if (response.data && Array.isArray(response.data.ledger)) {
                    dataList = response.data.ledger;
                } else if (Array.isArray(response.records)) {
                    dataList = response.records;
                } else if (Array.isArray(response.ledger)) {
                    dataList = response.ledger;
                } else if (Array.isArray(response.bookings)) {
                    dataList = response.bookings;
                } else {
                    const arrKey = Object.keys(response).find(k => Array.isArray(response[k]));
                    if (arrKey) dataList = response[arrKey];
                }
            }

            let paginationObj = {
                totalItems: dataList.length,
                totalPages: Math.ceil(dataList.length / currentLimit) || 1,
                currentPage: currentPage,
                limit: currentLimit,
                hasNextPage: false,
                hasPrevPage: false
            };

            const pagSource = response?.pagination || response?.data?.pagination || (response?.totalItems !== undefined || response?.total !== undefined || response?.totalPages !== undefined ? response : null);
            if (pagSource) {
                const total = pagSource.totalItems !== undefined ? pagSource.totalItems : (pagSource.total !== undefined ? pagSource.total : dataList.length);
                const pages = pagSource.totalPages !== undefined ? pagSource.totalPages : (Math.ceil(total / currentLimit) || 1);
                paginationObj = {
                    totalItems: Number(total) || 0,
                    totalPages: Number(pages) || 1,
                    currentPage: Number(pagSource.currentPage || pagSource.page || currentPage),
                    limit: Number(pagSource.limit || currentLimit),
                    hasNextPage: Boolean(pagSource.hasNextPage),
                    hasPrevPage: Boolean(pagSource.hasPrevPage)
                };
            }

            setLedgerList(dataList);
            setPagination(paginationObj);
            setSummary(response?.summary || response?.data?.summary || null);
        } catch (err) {
            console.error("Error fetching link breakdown:", err);
            setError(err.response?.data?.message || err.message || "Failed to load commission ledger records from server.");
            setLedgerList([]);
        } finally {
            setLoading(false);
        }
    }, [linkId, limit]);

    useEffect(() => {
        fetchLedger(page, limit);
    }, [page, limit, fetchLedger]);

    // Helper to extract clean ledger row data
    const getLedgerRowData = (item, idx) => {
        if (!item) return {};
        const id = item.id || item._id || item.ledgerId || `led-${idx}`;
        const bookingNumber = item.bookingId || item.bookingNumber || item.booking_number || item.booking?.bookingNumber || item.booking?.id || (idx + 1);
        
        const affiliateName = typeof item.affiliate === 'string'
            ? item.affiliate
            : (item.affiliate?.name || item.affiliateName || item.affiliate_name || item.user?.name || passedAffiliate?.name || passedLink?.affiliateName || '-');
            
        const retreatTitle = typeof item.retreat === 'string'
            ? item.retreat
            : (item.retreat?.title || item.retreat?.name || item.retreatTitle || item.retreat_title || item.retreatName || item.title || '-');
            
        const retreatPrice = Number(item.retreatPrice ?? item.retreat_price ?? item.price ?? item.totalPrice ?? item.booking?.totalPrice ?? item.amount ?? 0) || 0;
        const ourCommission = Number(item.ourCommission ?? item.our_commission ?? item.platformCommission ?? item.commission ?? item.companyCommission ?? 0) || 0;
        const tier = item.tier || (item.tierPercentage ? `${item.tierPercentage}%` : (item.commissionRate ? `${item.commissionRate}%` : (item.rate ? `${item.rate}%` : '-')));

        const rawEarns = item.affiliateEarns ?? item.affiliate_earns ?? item.affiliateEarnings ?? item.earnings ?? item.payout ?? item.affiliateAmount ?? item.commissionAmount;
        let affiliateEarnsValue = 0;
        if (typeof rawEarns === 'string' && rawEarns.startsWith('₹')) {
            affiliateEarnsValue = parseFloat(rawEarns.replace(/[^0-9.-]+/g, '')) || 0;
        } else {
            affiliateEarnsValue = parseFloat(rawEarns) || 0;
        }

        const rawState = (item.state || (item.status && item.status.includes('hold') ? 'on_hold' : item.status) || item.payoutStatus || 'pending').toLowerCase();
        const stateLabel = item.status || item.stateLabel || item.statusLabel;
        const refundAmount = item.refundAmount ?? item.refund_amount ?? item.refund ?? null;

        return {
            id,
            bookingNumber,
            affiliateName,
            retreatTitle,
            retreatPrice,
            ourCommission,
            tier,
            affiliateEarns: affiliateEarnsValue,
            state: rawState,
            stateLabel,
            refundAmount,
            cuName: item.cuName,
            uuName: item.uuName,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt
        };
    };

    const processedLedger = ledgerList.map(getLedgerRowData);
    const totalAffiliateEarns = summary?.totalEarnings ?? summary?.totalAffiliateEarns ?? processedLedger.reduce((sum, item) => sum + item.affiliateEarns, 0);

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/affiliates/links');
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Top Back Navigation & Header */}
            <Stack spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Button
                        startIcon={<ArrowLeft size={16} />}
                        onClick={handleBack}
                        disableElevation
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        sx={{
                            color: '#64748B',
                            textTransform: 'none',
                            fontSize: '0.82rem',
                            fontWeight: 500,
                            p: 0,
                            mb: 1.5,
                            minWidth: 'auto',
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: '#1E293B'
                            }
                        }}
                    >
                        Back to Links & Clicks
                    </Button>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1.5}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    color: '#1E293B',
                                    fontWeight: 600,
                                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Commission Ledger
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.82rem', mt: 0.5 }}>
                                {loading
                                    ? 'Loading ledger...'
                                    : `${pagination.totalItems || processedLedger.length} bookings · ${formatCurrency(totalAffiliateEarns)} affiliate earnings${passedLink?.refCode ? ` · Link: ${passedLink.refCode}` : passedAffiliate?.name ? ` · Filtered for: ${passedAffiliate.name}` : ''}`}
                            </Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => fetchLedger(page, limit)}
                            startIcon={<RefreshCw size={14} />}
                            disabled={loading}
                            sx={{
                                color: '#64748B',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                height: '36px',
                                px: 1.5,
                                borderRadius: '6px',
                                border: '1px solid #E2E8F0',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: '#F8FAFC',
                                    borderColor: '#CBD5E1',
                                    color: '#1E293B',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Box>

                {/* Commission States Notice Banner */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.75,
                        px: 2,
                        borderRadius: '6px',
                        border: '1px solid #E8E8E8',
                        borderLeft: '4px solid #803927',
                        bgcolor: '#FFFFFF',
                        boxShadow: 'none'
                    }}
                >
                    <Typography sx={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
                        <strong style={{ color: '#1E293B', fontWeight: 600 }}>Commission states:</strong> pending (booked) → confirmed (retreat completed) → paid. Cancelled or refunded before completion → reversed. Flagged lines sit on hold until admin clears them.
                    </Typography>
                </Paper>
            </Stack>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '6px', fontSize: '0.82rem', boxShadow: 'none' }}>
                    {error}
                </Alert>
            )}

            {/* Commission Ledger Table */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    bgcolor: '#FFFFFF',
                    overflow: "hidden",
                    boxShadow: 'none'
                }}
            >
                <TableContainer>
                    <Table sx={{ minWidth: 1400 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, width: '8%' }}>
                                    Booking
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '13%' }}>
                                    Affiliate
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '13%' }}>
                                    Retreat
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '11%' }}>
                                    Retreat Price
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '11%' }}>
                                    Our Commission
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, textAlign: 'center', width: '6%' }}>
                                    Tier
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '11%' }}>
                                    Affiliate Earns
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, textAlign: 'center', width: '8%' }}>
                                    State
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, textAlign: 'right', width: '9%' }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={13} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : processedLedger.length > 0 ? (
                                processedLedger.map((item) => {
                                    const status = getStatusDetails(item.state, item.stateLabel);
                                    return (
                                        <TableRow
                                            key={item.id}
                                            sx={{
                                                '& td': { borderBottom: '1px solid #F0F0F0' },
                                                '&:last-child td': { borderBottom: 0 },
                                            }}
                                        >
                                            {/* Booking Number */}
                                            <TableCell sx={{ py: 1.4, px: 1.5, fontSize: '0.8rem', fontWeight: 600, color: '#1E293B' }}>
                                                {item.bookingNumber ? String(item.bookingNumber).replace(/^#/, '') : '-'}
                                            </TableCell>

                                            {/* Affiliate */}
                                            <TableCell sx={{ py: 1.4, px: 1.25 }}>
                                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {item.affiliateName}
                                                </Typography>
                                            </TableCell>

                                            {/* Retreat Title */}
                                            <TableCell sx={{ py: 1.4, px: 1.25 }}>
                                                <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
                                                    {item.retreatTitle}
                                                </Typography>
                                            </TableCell>

                                            {/* Retreat Price */}
                                            <TableCell sx={{ py: 1.4, px: 1.25, fontSize: '0.78rem', color: '#1E293B', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {formatCurrency(item.retreatPrice)}
                                            </TableCell>

                                            {/* Our Commission */}
                                            <TableCell sx={{ py: 1.4, px: 1.25, fontSize: '0.78rem', color: '#475569', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {formatCurrency(item.ourCommission)}
                                            </TableCell>

                                            {/* Tier */}
                                            <TableCell sx={{ py: 1.4, px: 1, textAlign: 'center', fontSize: '0.78rem', color: '#334155', fontWeight: 500 }}>
                                                {item.tier}
                                            </TableCell>

                                            {/* Affiliate Earns */}
                                            <TableCell sx={{ py: 1.4, px: 1.25, fontSize: '0.78rem', color: '#1E293B', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                {formatCurrency(item.affiliateEarns)}
                                            </TableCell>

                                            {/* State */}
                                            <TableCell sx={{ py: 1.4, px: 1, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        px: 1.2,
                                                        py: 0.35,
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        display: 'inline-block',
                                                        textTransform: 'capitalize',
                                                        bgcolor: status.bg,
                                                        color: status.color,
                                                        border: `1px solid ${status.border}`
                                                    }}
                                                >
                                                    {status.label}
                                                </Box>
                                            </TableCell>

                                            {/* Created By */}
                                            <TableCell sx={{ py: 1.4, px: 1.5, fontSize: '0.78rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>

                                            {/* Updated By */}
                                            <TableCell sx={{ py: 1.4, px: 1.5, fontSize: '0.78rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>

                                            {/* Created At */}
                                            <TableCell sx={{ py: 1.4, px: 1.5, fontSize: '0.78rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt)}
                                            </TableCell>

                                            {/* Updated At */}
                                            <TableCell sx={{ py: 1.4, px: 1.5, fontSize: '0.78rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.updatedAt)}
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="right" sx={{ py: 1.4, px: 1.25, whiteSpace: 'nowrap' }}>
                                                {item.refundAmount ? (
                                                    <Typography sx={{ fontSize: '0.74rem', color: '#64748B', fontFamily: 'monospace' }}>
                                                        Refund {formatCurrency(item.refundAmount)}
                                                    </Typography>
                                                ) : (
                                                    <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8' }}>
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={13} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.78rem' }}>
                                        No ledger records found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination Controls directly driven by Backend API */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={pagination.totalItems || processedLedger.length}
                totalPages={pagination.totalPages}
                onPageChange={(newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newLimit) => {
                    setLimit(newLimit);
                    setPage(1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                loading={loading}
            />
        </Box>
    );
};

export default AffiliateCommissionLedger;
